import { javascript } from "@codemirror/lang-javascript";
import { json as jsonLanguage } from "@codemirror/lang-json";
import { python } from "@codemirror/lang-python";
import { sql as sqlLanguage } from "@codemirror/lang-sql";
import { EditorView, type Extension } from "@uiw/react-codemirror";
import { EDITOR_SURFACE } from "../../common/editor-theme";
import type { LanguageId } from "../../common/schema/languages";
import type { FormatId } from "./write";

export function schemaExtensions(language: LanguageId): Extension[] {
  return SCHEMA_EXTENSIONS[language];
}

export function outputExtensions(format: FormatId): Extension[] {
  return OUTPUT_EXTENSIONS[format];
}

// Out here so their identity never changes: a new array reconfigures the running editor.
const BASE = [EditorView.lineWrapping, ...EDITOR_SURFACE];

// Nobody types into the batch — it is written by the page and rewritten whenever anything above it moves, so an edit
// would be gone by the next keystroke. Not editable is not the same as not reachable: the caret, the selection, the
// search panel and the copy all still work, which is the whole of what this pane is for.
const READ_ONLY = [EditorView.editable.of(false), ...BASE];

const SCHEMA_EXTENSIONS: Record<LanguageId, Extension[]> = {
  "json-schema": [jsonLanguage(), EditorView.contentAttributes.of({ "aria-label": "JSON Schema" }), ...BASE],
  zod: [javascript({ typescript: true }), EditorView.contentAttributes.of({ "aria-label": "Zod schema" }), ...BASE],
  pydantic: [python(), EditorView.contentAttributes.of({ "aria-label": "Pydantic model" }), ...BASE],
};

const OUTPUT_EXTENSIONS: Record<FormatId, Extension[]> = {
  json: [jsonLanguage(), EditorView.contentAttributes.of({ "aria-label": "Generated JSON" }), ...READ_ONLY],
  // NDJSON is a JSON value per line and not a JSON document, so the mode would call every line after the first an
  // error; it is left plain rather than underlined end to end.
  ndjson: [EditorView.contentAttributes.of({ "aria-label": "Generated NDJSON" }), ...READ_ONLY],
  csv: [EditorView.contentAttributes.of({ "aria-label": "Generated CSV" }), ...READ_ONLY],
  sql: [sqlLanguage(), EditorView.contentAttributes.of({ "aria-label": "Generated SQL" }), ...READ_ONLY],
};
